import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Bell,
  Calendar,
  CalendarDays,
  Clock3,
  EyeOff,
  Inbox,
  List,
  ListTodo,
  LogOut,
  Menu,
  MoreHorizontal,
  Move,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  Square,
  SquareCheckBig,
  Trash2,
  Undo2,
  X,
  Zap,
} from 'lucide-react';

const Api = window.TaskFlowApi;
const Dates = window.TaskFlowDates;
const pausedTimerStorageKey = 'taskflow:pausedTimer';
const timerPositionStorageKey = 'taskflow:timerPosition';

const icons = {
  inbox: Inbox,
  today: CalendarDays,
  upcoming: Zap,
  overdue: Clock3,
  all: ListTodo,
  completed: SquareCheckBig,
  list: List,
  settings: Settings,
  plus: Plus,
  search: Search,
  play: Play,
  pause: Pause,
  stop: Square,
  clock: Clock3,
  bell: Bell,
  calendar: Calendar,
  trash: Trash2,
  edit: Pencil,
  x: X,
  logout: LogOut,
  save: Save,
  undo: Undo2,
  menu: Menu,
  refresh: RefreshCw,
  eyeOff: EyeOff,
  more: MoreHorizontal,
  move: Move,
};

const smartViews = [
  { id: 'inbox', label: 'Inbox', icon: 'inbox' },
  { id: 'today', label: 'Today', icon: 'today' },
  { id: 'upcoming', label: 'Upcoming', icon: 'upcoming' },
  { id: 'overdue', label: 'Overdue', icon: 'overdue' },
  { id: 'all', label: 'All tasks', icon: 'all' },
  { id: 'completed', label: 'Completed', icon: 'completed' },
];

function cx(...values) {
  return values.filter(Boolean).join(' ');
}

function defaultTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
}

function pushSubscriptionPayload(subscription) {
  const encodings = window.PushManager?.supportedContentEncodings || [];
  return {
    ...subscription.toJSON(),
    contentEncoding: encodings[0] || 'aesgcm',
    device_label: navigator.platform || 'This browser',
  };
}

function emptyDrawer() {
  return {
    text: '',
    notes: '',
    list_id: null,
    priority: 'normal',
    scheduled_start_local: '',
    is_all_day: false,
    due_at_local: '',
    due_date: '',
    timezone: defaultTimezone(),
    estimated_minutes: '',
    reminder_offsets: [-1440, -30, 0],
    calendar_sync_enabled: false,
    completed: false,
    deleted_at_utc: null,
    time_entries: [],
    reminders: [],
  };
}

function Icon({ name, className = '' }) {
  const LucideIcon = icons[name] || List;
  return <LucideIcon className={cx('icon', className)} aria-hidden="true" />;
}

function FieldError({ message }) {
  return message ? <span className="field-error">{message}</span> : null;
}

function DateInput({ type, value, onChange, placeholder, className = 'input', ...props }) {
  const [focused, setFocused] = useState(false);
  const hasValue = value !== null && value !== undefined && value !== '';
  const activeType = focused || hasValue ? type : 'text';

  return (
    <input
      {...props}
      className={className}
      type={activeType}
      value={value || ''}
      onPointerDown={(event) => {
        setFocused(true);
        props.onPointerDown?.(event);
      }}
      onFocus={(event) => {
        setFocused(true);
        props.onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        props.onBlur?.(event);
      }}
      onChange={onChange}
      placeholder={placeholder}
    />
  );
}

function readStoredJson(key) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch (err) {
    return null;
  }
}

function writeStoredJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Storage can be blocked; the live timer state still works for this session.
  }
}

function removeStoredItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    // Ignore storage cleanup failures.
  }
}

function restorePausedTimer() {
  const timer = readStoredJson(pausedTimerStorageKey);
  if (!timer || !Number(timer.task_id)) return null;
  return {
    ...timer,
    task_id: Number(timer.task_id),
    elapsed_seconds: Math.max(0, Math.floor(Number(timer.elapsed_seconds || 0))),
    paused: true,
  };
}

function restoreTimerPosition() {
  const position = readStoredJson(timerPositionStorageKey);
  const x = Number(position?.x);
  const y = Number(position?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function timerPositionStyle(position) {
  if (!position) return undefined;
  return {
    '--timer-x': `${Math.round(position.x)}px`,
    '--timer-y': `${Math.round(position.y)}px`,
  };
}

function taskIdOf(task) {
  return Number(task?.task_id || task?.id || 0);
}

function timerTitleOf(task) {
  return task?.task_title || task?.title || task?.text || 'Focus timer';
}

function pausedTimerFrom(task, elapsed) {
  return {
    id: task?.id || null,
    task_id: taskIdOf(task),
    task_title: timerTitleOf(task),
    estimated_minutes: task?.estimated_minutes || 25,
    elapsed_seconds: Math.max(0, Math.floor(Number(elapsed || 0))),
    paused: true,
    paused_at: Date.now(),
  };
}

function elapsedFromRunningTask(task) {
  const startedAt = task?.running_timer_started_at_utc || task?.started_at_utc;
  const started = Dates.parseUtc(startedAt);
  if (!started) return 0;
  return Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));
}

function canDragTimer() {
  return window.matchMedia('(min-width: 821px)').matches;
}

function clampTimerPosition(position, rect) {
  const margin = 12;
  const width = rect?.width || 420;
  const height = rect?.height || 220;
  return {
    x: Math.min(Math.max(margin, position.x), Math.max(margin, window.innerWidth - width - margin)),
    y: Math.min(Math.max(margin, position.y), Math.max(margin, window.innerHeight - height - margin)),
  };
}

function samePosition(first, second) {
  if (!first || !second) return first === second;
  return Math.round(first.x) === Math.round(second.x) && Math.round(first.y) === Math.round(second.y);
}

let timerAudioContext = null;

function getTimerAudioContext() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!timerAudioContext) {
    timerAudioContext = new AudioContextClass();
  }
  return timerAudioContext;
}

function unlockTimerSound() {
  try {
    const audio = getTimerAudioContext();
    if (!audio) return;
    if (audio.state === 'suspended') {
      audio.resume().catch(() => {});
    }
    const gain = audio.createGain();
    gain.gain.value = 0;
    gain.connect(audio.destination);
    const oscillator = audio.createOscillator();
    oscillator.connect(gain);
    oscillator.start();
    oscillator.stop(audio.currentTime + 0.01);
  } catch {
    // Browser audio can be blocked until a user gesture; the timer still works.
  }
}

function playTimerFinishedSound() {
  try {
    const audio = getTimerAudioContext();
    if (!audio) return false;
    if (audio.state === 'suspended') {
      audio.resume().catch(() => {});
    }

    const notes = [659.25, 783.99, 987.77];
    notes.forEach((frequency, index) => {
      const start = audio.currentTime + 0.04 + index * 0.18;
      const gain = audio.createGain();
      const oscillator = audio.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.16, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.26);
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.28);
    });

    if ('vibrate' in navigator) {
      navigator.vibrate([160, 70, 160]);
    }
    return true;
  } catch {
    return false;
  }
}

function calendarReturnFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('calendar');
  if (!status) return null;

  return {
    status,
    message: params.get('message') || '',
  };
}

function clearCalendarReturnFromUrl() {
  const params = new URLSearchParams(window.location.search);
  params.delete('calendar');
  params.delete('message');
  const nextQuery = params.toString();
  const nextUrl = window.location.pathname + (nextQuery ? `?${nextQuery}` : '') + window.location.hash;
  window.history.replaceState({}, '', nextUrl);
}

function App() {
  const [booting, setBooting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);
  const [session, setSession] = useState({ authenticated: false, user: null });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [setupForm, setSetupForm] = useState({
    setup_token: '',
    email: '',
    display_name: 'Owner',
    password: '',
    timezone: defaultTimezone(),
  });
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [lists, setLists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [smartCounts, setSmartCounts] = useState({});
  const [notifications, setNotifications] = useState({ items: [], unread_count: 0 });
  const [calendar, setCalendar] = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [pausedTimer, setPausedTimer] = useState(() => restorePausedTimer());
  const [timerLoadedAt, setTimerLoadedAt] = useState(Date.now());
  const [timerCarry, setTimerCarry] = useState({ taskId: null, seconds: 0 });
  const [timerTotals, setTimerTotals] = useState({ today_seconds: 0, week_seconds: 0 });
  const [timerPosition, setTimerPosition] = useState(() => restoreTimerPosition());
  const [selectedView, setSelectedView] = useState(localStorage.getItem('taskflow:selectedView') || 'today');
  const [selectedListId, setSelectedListId] = useState(Number(localStorage.getItem('taskflow:selectedListId') || 0) || null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('due');
  const [quickTitle, setQuickTitle] = useState('');
  const [quickBusy, setQuickBusy] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('task');
  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const [drawer, setDrawer] = useState(emptyDrawer);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [undoTaskId, setUndoTaskId] = useState(null);
  const [tick, setTick] = useState(Date.now());
  const [dialog, setDialog] = useState(null);
  const [focusPanelHidden, setFocusPanelHidden] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const queryTimer = useRef(null);
  const dialogResolver = useRef(null);
  const lastTimerTaskId = useRef(null);
  const calendarReturn = useRef(calendarReturnFromLocation());
  const timerAlarm = useRef({ key: null, previousRemaining: null, fired: false });

  const selectedList = useMemo(
    () => lists.find((list) => Number(list.id) === Number(selectedListId)),
    [lists, selectedListId]
  );
  const viewTitle = selectedView === 'list'
    ? selectedList?.name || 'List'
    : smartViews.find((view) => view.id === selectedView)?.label || 'Today';
  const activeViewCount = selectedView === 'list'
    ? Number(selectedList?.active_count || 0)
    : Number(smartCounts[selectedView] || 0);
  const viewSubtitle = useMemo(() => {
    const count = activeViewCount;
    const word = count === 1 ? 'task' : 'tasks';
    if (query.trim()) return `${tasks.length} ${tasks.length === 1 ? 'match' : 'matches'} for "${query.trim()}"`;
    if (selectedView === 'completed') return `${count} completed ${word}`;
    if (selectedView === 'inbox') return `${count} unsorted ${word}`;
    if (selectedView === 'overdue') return `${count} overdue ${word}`;
    if (selectedView === 'upcoming') return `${count} scheduled ${word}`;
    if (selectedView === 'all') return `${count} open ${word}`;
    return `${count} open ${word}`;
  }, [activeViewCount, query, selectedView, tasks.length]);
  const taskGroups = useMemo(() => buildTaskGroups(tasks, selectedView), [tasks, selectedView]);
  const activeTimerElapsed = useMemo(() => {
    if (!activeTimer) return 0;
    const carriedSeconds = Number(activeTimer.task_id) === Number(timerCarry.taskId) ? Number(timerCarry.seconds || 0) : 0;
    return Math.max(0, carriedSeconds + Number(activeTimer.elapsed_seconds || 0) + Math.floor((tick - timerLoadedAt) / 1000));
  }, [activeTimer, tick, timerLoadedAt, timerCarry]);
  const visibleTimer = activeTimer || pausedTimer;
  const visibleTimerElapsed = activeTimer ? activeTimerElapsed : Math.max(0, Number(pausedTimer?.elapsed_seconds || 0));
  const visibleTimerPaused = Boolean(!activeTimer && pausedTimer);
  const visibleTimerTaskId = visibleTimer ? Number(visibleTimer.task_id) : null;
  const theme = useMemo(() => {
    const themeName = settings?.theme || 'system';
    if (themeName !== 'system') return themeName;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, [settings]);
  const unreadCount = Number(notifications?.unread_count || 0);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    const interval = setInterval(() => setTick(Date.now()), 1000);
    const online = () => loadTasks();
    const offline = () => setError('You are offline. Changes will wait until the connection is back.');
    const keydown = (event) => {
      if (event.key === 'Escape') {
        setNotificationOpen(false);
        setSidebarOpen(false);
        setDrawerOpen(false);
        setContextMenu(null);
      }
    };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    window.addEventListener('keydown', keydown);
    boot();
    return () => {
      clearInterval(interval);
      clearTimeout(queryTimer.current);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      window.removeEventListener('keydown', keydown);
    };
  }, []);

  useEffect(() => {
    const taskId = activeTimer?.task_id || null;
    if (taskId && taskId !== lastTimerTaskId.current) {
      setFocusPanelHidden(false);
    }
    lastTimerTaskId.current = taskId;
  }, [activeTimer?.task_id]);

  useEffect(() => {
    if (!activeTimer && timerCarry.taskId !== null) {
      setTimerCarry({ taskId: null, seconds: 0 });
      return;
    }
    if (activeTimer && timerCarry.taskId !== null && Number(activeTimer.task_id) !== Number(timerCarry.taskId)) {
      setTimerCarry({ taskId: null, seconds: 0 });
    }
  }, [activeTimer?.task_id, timerCarry.taskId]);

  useEffect(() => {
    if (!timerPosition) return undefined;
    const clampSavedTimerPosition = () => {
      if (!canDragTimer()) return;
      const nextPosition = clampTimerPosition(timerPosition);
      if (!samePosition(nextPosition, timerPosition)) {
        saveTimerPosition(nextPosition);
      }
    };
    window.addEventListener('resize', clampSavedTimerPosition);
    clampSavedTimerPosition();
    return () => window.removeEventListener('resize', clampSavedTimerPosition);
  }, [timerPosition]);

  useEffect(() => {
    if (!activeTimer) {
      timerAlarm.current = { key: null, previousRemaining: null, fired: false };
      return;
    }

    const timerKey = activeTimer.id || `${activeTimer.task_id}:${activeTimer.started_at_utc || ''}`;
    const targetSeconds = Math.max(60, Number(activeTimer.estimated_minutes || 25) * 60);
    const remaining = targetSeconds - activeTimerElapsed;
    const current = timerAlarm.current;

    if (current.key !== timerKey) {
      timerAlarm.current = {
        key: timerKey,
        previousRemaining: remaining,
        fired: remaining <= 0,
      };
      return;
    }

    if (!current.fired && Number(current.previousRemaining) > 0 && remaining <= 0) {
      current.fired = true;
      playTimerFinishedSound();
      showToast(`${activeTimer.task_title || 'Timer'} reached its estimate`);
    }

    current.previousRemaining = remaining;
  }, [
    activeTimer?.id,
    activeTimer?.task_id,
    activeTimer?.started_at_utc,
    activeTimer?.estimated_minutes,
    activeTimer?.task_title,
    activeTimerElapsed,
  ]);

  async function boot() {
    setBooting(true);
    try {
      const data = await loadSession();
      if (data.authenticated) {
        await loadBootstrap();
        await loadTasks({ view: selectedView, listId: selectedListId, force: true });
      }
      await settleCalendarReturn(Boolean(data.authenticated));
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('sw.js').catch(() => {});
      }
    } catch (err) {
      handleError(err);
    } finally {
      setBooting(false);
    }
  }

  async function loadSession() {
    const data = await Api.get('/session');
    setSetupRequired(data.setup_required);
    setSession({ authenticated: data.authenticated, user: data.user });
    Api.setCsrf(data.csrf_token);
    return data;
  }

  async function settleCalendarReturn(isAuthenticated) {
    const result = calendarReturn.current;
    if (!result) return;

    calendarReturn.current = null;
    clearCalendarReturnFromUrl();

    if (result.status === 'error') {
      setError(result.message || 'Google Calendar connection failed. Check storage/logs/taskflow.log on the server.');
      return;
    }

    if (result.status !== 'connected') return;

    if (!isAuthenticated) {
      setError('Sign in again, then connect Google Calendar once more.');
      return;
    }

    try {
      const status = await Api.get('/calendar/status');
      setCalendar(status);
      if (status?.connection?.status === 'connected') {
        showToast('Google Calendar connected');
        setError('');
      } else {
        setError('Google approved access, but TaskFlow did not save the Calendar connection. Check storage/logs/taskflow.log on the server, then reconnect.');
      }
    } catch (err) {
      handleError(err);
    }
  }

  async function setupOwner(event) {
    event.preventDefault();
    clearErrors();
    setSaving(true);
    try {
      await withBusy('Creating owner...', async () => {
        const data = await Api.post('/auth/setup', setupForm);
        setSession({ authenticated: true, user: data.user });
        Api.setCsrf(data.csrf_token);
        setSetupRequired(false);
        await loadBootstrap();
        await loadTasks({ view: selectedView, listId: selectedListId, force: true });
      });
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function login(event) {
    event.preventDefault();
    clearErrors();
    setSaving(true);
    try {
      await withBusy('Signing in...', async () => {
        const data = await Api.post('/auth/login', loginForm);
        setSession({ authenticated: true, user: data.user });
        Api.setCsrf(data.csrf_token);
        await loadBootstrap();
        await loadTasks({ view: selectedView, listId: selectedListId, force: true });
      });
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function logout() {
    clearErrors();
    try {
      await withBusy('Signing out...', async () => {
        await Api.post('/auth/logout', {});
        setSession({ authenticated: false, user: null });
        setTasks([]);
        setLists([]);
        setActiveTimer(null);
        setPausedTimerState(null);
        setTimerCarry({ taskId: null, seconds: 0 });
        await loadSession();
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function loadBootstrap() {
    setLoading(true);
    try {
      const data = await Api.get('/bootstrap');
      applyBootstrap(data);
      setError('');
      setFieldErrors({});
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadTasks(overrides = {}) {
    if (!session.authenticated && !overrides.force) return;
    setLoading(true);
    const view = overrides.view ?? selectedView;
    const listId = overrides.listId ?? selectedListId;
    const q = overrides.query ?? query;
    const order = overrides.sort ?? sort;
    try {
      const params = { view, q, sort: order };
      if (view === 'list') params.list_id = listId;
      const data = await Api.get('/tasks', params);
      setTasks(data.tasks || []);
      const bootData = await Api.get('/bootstrap');
      applyBootstrap({ ...bootData, tasks: data.tasks || [] });
      setError('');
      setFieldErrors({});
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }

  function applyBootstrap(data) {
    setSettings(data.settings);
    setSettingsDraft({
      ...data.settings,
      default_reminder_offsets: [...(data.settings?.default_reminder_offsets || [])],
    });
    setLists(data.lists || []);
    setSmartCounts(data.smart_counts || {});
    setTasks(data.tasks || []);
    setNotifications(data.notifications || { items: [], unread_count: 0 });
    const serverActiveTimer = data.active_timer || null;
    setActiveTimer(serverActiveTimer);
    if (serverActiveTimer) {
      setPausedTimerState(null);
    }
    setTimerLoadedAt(Date.now());
    setTimerTotals(data.timer_totals || { today_seconds: 0, week_seconds: 0 });
    setCalendar(data.calendar);
  }

  function setPausedTimerState(timer) {
    setPausedTimer(timer);
    if (timer) {
      writeStoredJson(pausedTimerStorageKey, timer);
    } else {
      removeStoredItem(pausedTimerStorageKey);
    }
  }

  function saveTimerPosition(position) {
    setTimerPosition(position);
    if (position) {
      writeStoredJson(timerPositionStorageKey, position);
    } else {
      removeStoredItem(timerPositionStorageKey);
    }
  }

  function selectView(id) {
    setContextMenu(null);
    setSelectedView(id);
    setSelectedListId(null);
    localStorage.setItem('taskflow:selectedView', id);
    localStorage.removeItem('taskflow:selectedListId');
    setSidebarOpen(false);
    loadTasks({ view: id, listId: null });
  }

  function selectList(id) {
    setContextMenu(null);
    const numericId = Number(id);
    setSelectedView('list');
    setSelectedListId(numericId);
    localStorage.setItem('taskflow:selectedView', 'list');
    localStorage.setItem('taskflow:selectedListId', String(numericId));
    setSidebarOpen(false);
    loadTasks({ view: 'list', listId: numericId });
  }

  function queueLoadTasks(nextQuery) {
    setQuery(nextQuery);
    clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => {
      loadTasks({ view: selectedView, listId: selectedListId, query: nextQuery });
    }, 220);
  }

  function clearSearch() {
    clearTimeout(queryTimer.current);
    setQuery('');
    loadTasks({ view: selectedView, listId: selectedListId, query: '' });
  }

  async function quickAdd(event) {
    event.preventDefault();
    const text = quickTitle.trim();
    if (!text || quickBusy) return;
    clearErrors();
    setQuickBusy(true);
    try {
      await withBusy('Adding task...', async () => {
        const data = await Api.post('/tasks', { text, list_id: currentListIdForCreate() });
        setQuickTitle('');
        setTasks((current) => [data.task, ...current]);
        showToast('Task added');
        await loadTasks();
      });
    } catch (err) {
      handleError(err);
    } finally {
      setQuickBusy(false);
    }
  }

  async function createList() {
    clearErrors();
    const name = await openDialog({
      type: 'list-name',
      title: 'New list',
      label: 'List name',
      initialValue: '',
      confirmLabel: 'Create list',
    });
    if (!name) return;
    try {
      await withBusy('Creating list...', async () => {
        const data = await Api.post('/lists', { name });
        const numericId = Number(data.list.id);
        setLists((current) => [...current, data.list]);
        setSelectedView('list');
        setSelectedListId(numericId);
        localStorage.setItem('taskflow:selectedView', 'list');
        localStorage.setItem('taskflow:selectedListId', String(numericId));
        setSidebarOpen(false);
        await loadTasks({ view: 'list', listId: numericId });
        showToast('List added');
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function renameList(list) {
    clearErrors();
    const name = await openDialog({
      type: 'list-name',
      title: 'Rename list',
      label: 'List name',
      initialValue: list.name,
      confirmLabel: 'Save name',
    });
    if (!name || name === list.name) return;
    try {
      await withBusy('Renaming list...', async () => {
        await Api.patch(`/lists/${list.id}`, { name });
        await loadTasks();
        showToast('List renamed');
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function deleteListFlow(list) {
    clearErrors();
    const result = await openDialog({ type: 'delete-list', list, lists });
    if (!result) return;
    try {
      await withBusy('Deleting list...', async () => {
        await Api.delete(`/lists/${list.id}`, result);
        if (Number(selectedListId) === Number(list.id)) {
          setSelectedView('inbox');
          setSelectedListId(null);
          localStorage.setItem('taskflow:selectedView', 'inbox');
          localStorage.removeItem('taskflow:selectedListId');
          setSidebarOpen(false);
          await loadTasks({ view: 'inbox', listId: null });
        } else {
          await loadTasks();
        }
        showToast('List deleted');
      });
    } catch (err) {
      handleError(err);
    }
  }

  function openNewDetailsForList(listId) {
    clearErrors();
    setContextMenu(null);
    setDrawerTaskId(null);
    setDrawerMode('task');
    setDrawer({
      ...emptyDrawer(),
      list_id: listId ?? currentListIdForCreate(),
      timezone: settings?.timezone || setupForm.timezone,
      reminder_offsets: [...(settings?.default_reminder_offsets || [-1440, -30, 0])],
      calendar_sync_enabled: !!settings?.default_calendar_sync_enabled,
    });
    setDrawerOpen(true);
  }

  function openNewDetails() {
    openNewDetailsForList(currentListIdForCreate());
  }

  async function openTask(task) {
    clearErrors();
    setContextMenu(null);
    setDrawerMode('task');
    setDrawerOpen(true);
    setDrawerTaskId(Number(task.id));
    setDrawer({ ...emptyDrawer(), ...task });
    try {
      await withBusy('Opening task...', async () => {
        const data = await Api.get(`/tasks/${task.id}`);
        const full = data.task;
        setDrawer({
          ...emptyDrawer(),
          ...full,
          list_id: full.list_id || null,
          reminder_offsets: offsetsFromReminders(full.reminders),
          estimated_minutes: full.estimated_minutes || '',
          scheduled_start_local: full.scheduled_start_local || '',
          due_at_local: full.due_at_local || '',
          due_date: full.due_date || '',
        });
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function saveDrawer() {
    clearErrors();
    setSaving(true);
    try {
      const payload = {
        text: drawer.text,
        notes: drawer.notes || null,
        list_id: drawer.list_id || null,
        priority: drawer.priority || 'normal',
        scheduled_start_local: drawer.scheduled_start_local || '',
        is_all_day: !!drawer.is_all_day,
        due_at_local: drawer.is_all_day ? '' : (drawer.due_at_local || ''),
        due_date: drawer.is_all_day ? (drawer.due_date || '') : '',
        timezone: drawer.timezone || settings?.timezone,
        estimated_minutes: drawer.estimated_minutes || null,
        reminder_offsets: drawer.reminder_offsets || [],
        calendar_sync_enabled: !!drawer.calendar_sync_enabled,
      };
      await withBusy(drawerTaskId ? 'Saving task...' : 'Creating task...', async () => {
        const data = drawerTaskId
          ? await Api.patch(`/tasks/${drawerTaskId}`, payload)
          : await Api.post('/tasks', payload);
        setDrawerTaskId(Number(data.task.id));
        setDrawer((current) => ({ ...current, ...data.task }));
        showToast('Saved');
        await loadTasks();
      });
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function toggleComplete(task) {
    clearErrors();
    setContextMenu(null);
    try {
      await withBusy(task.completed ? 'Reopening task...' : 'Completing task...', async () => {
        await Api.patch(`/tasks/${task.id}`, { completed: !task.completed });
        await loadTasks();
      });
    } catch (err) {
      if (err.code === 'timer_running_on_complete') {
        const confirmed = await openDialog({
          type: 'confirm',
          title: 'Stop timer and complete?',
          message: 'This task has a running timer. TaskFlow will save the tracked time before marking it complete.',
          confirmLabel: 'Stop and complete',
          tone: 'primary',
        });
        if (confirmed) {
          try {
            await withBusy('Stopping timer and completing task...', async () => {
              await Api.patch(`/tasks/${task.id}`, { completed: true, stop_timer: true });
              await loadTasks();
            });
          } catch (nextErr) {
            handleError(nextErr);
          }
        }
        return;
      }
      handleError(err);
    }
  }

  async function deleteTask(task) {
    clearErrors();
    setContextMenu(null);
    const confirmed = await openDialog({
      type: 'confirm',
      title: 'Delete task?',
      message: 'The task will move to the trash and can be restored right after deleting.',
      confirmLabel: 'Delete task',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await withBusy('Deleting task...', async () => {
        await Api.delete(`/tasks/${task.id}`, {});
        setUndoTaskId(Number(task.id));
        showToast('Task deleted', 'Undo');
        setDrawerOpen(false);
        await loadTasks();
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function restoreUndo() {
    if (!undoTaskId) return;
    try {
      await withBusy('Restoring task...', async () => {
        await Api.post(`/tasks/${undoTaskId}/restore`, {});
        setUndoTaskId(null);
        setToast(null);
        await loadTasks();
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function timerAction(task) {
    clearErrors();
    setContextMenu(null);
    const taskId = taskIdOf(task);
    if (!taskId) return;
    try {
      const pausedThis = pausedTimer && Number(pausedTimer.task_id) === taskId && !activeTimer;
      if (pausedThis) {
        await resumePausedTimer(pausedTimer);
        return;
      }

      const runningThis = task.running_timer_started_at_utc || (activeTimer && Number(activeTimer.task_id) === taskId);
      if (runningThis) {
        await pauseTimer(task);
        return;
      } else {
        unlockTimerSound();
        try {
          await withBusy('Starting timer...', async () => {
            const data = await Api.post(`/tasks/${taskId}/timer/start`, {});
            setActiveTimer(data?.task_id ? data : null);
            setPausedTimerState(null);
            setTimerCarry({ taskId: null, seconds: 0 });
            setTimerLoadedAt(Date.now());
            setFocusPanelHidden(false);
          });
        } catch (err) {
          if (err.code === 'timer_already_running') {
            const confirmed = await openDialog({
              type: 'confirm',
              title: 'Switch focus timer?',
              message: 'TaskFlow will save the time on the current task and start this one.',
              confirmLabel: 'Switch timer',
              tone: 'primary',
            });
            if (confirmed) {
              unlockTimerSound();
              await withBusy('Switching timer...', async () => {
                const data = await Api.post(`/tasks/${taskId}/timer/start`, { stop_current: true });
                setActiveTimer(data?.task_id ? data : null);
                setPausedTimerState(null);
                setTimerCarry({ taskId: null, seconds: 0 });
                setTimerLoadedAt(Date.now());
                setFocusPanelHidden(false);
              });
            } else {
              return;
            }
          } else {
            throw err;
          }
        }
      }
      await loadTasks();
    } catch (err) {
      handleError(err);
    }
  }

  async function pauseTimer(task = activeTimer) {
    const taskId = taskIdOf(task);
    if (!taskId) return;
    clearErrors();
    setContextMenu(null);
    const elapsed = activeTimer && Number(activeTimer.task_id) === taskId
      ? activeTimerElapsed
      : elapsedFromRunningTask(task);
    const timer = pausedTimerFrom(activeTimer && Number(activeTimer.task_id) === taskId ? activeTimer : task, elapsed);
    try {
      await withBusy('Pausing timer...', async () => {
        await Api.post(`/tasks/${taskId}/timer/pause`, {});
        setPausedTimerState(timer);
        setActiveTimer(null);
        setTimerCarry({ taskId: null, seconds: 0 });
        setTimerLoadedAt(Date.now());
        setFocusPanelHidden(false);
        await loadTasks();
      });
      showToast('Timer paused');
    } catch (err) {
      handleError(err);
    }
  }

  async function resumePausedTimer(timer = pausedTimer) {
    const taskId = taskIdOf(timer);
    if (!taskId) return;
    clearErrors();
    setContextMenu(null);
    unlockTimerSound();
    try {
      await withBusy('Resuming timer...', async () => {
        const data = await Api.post(`/tasks/${taskId}/timer/resume`, {});
        setTimerCarry({ taskId, seconds: Number(timer.elapsed_seconds || 0) });
        setActiveTimer(data?.task_id ? data : null);
        setPausedTimerState(null);
        setTimerLoadedAt(Date.now());
        setFocusPanelHidden(false);
        await loadTasks();
      });
    } catch (err) {
      if (err.code === 'timer_already_running') {
        const confirmed = await openDialog({
          type: 'confirm',
          title: 'Switch focus timer?',
          message: 'TaskFlow will save the time on the current task and resume this one.',
          confirmLabel: 'Switch timer',
          tone: 'primary',
        });
        if (!confirmed) return;
        try {
          await withBusy('Switching timer...', async () => {
            const data = await Api.post(`/tasks/${taskId}/timer/resume`, { stop_current: true });
            setTimerCarry({ taskId, seconds: Number(timer.elapsed_seconds || 0) });
            setActiveTimer(data?.task_id ? data : null);
            setPausedTimerState(null);
            setTimerLoadedAt(Date.now());
            setFocusPanelHidden(false);
            await loadTasks();
          });
        } catch (nextErr) {
          handleError(nextErr);
        }
        return;
      }
      handleError(err);
    }
  }

  async function stopActiveTimer() {
    if (!activeTimer) {
      if (pausedTimer) {
        setPausedTimerState(null);
        setTimerCarry({ taskId: null, seconds: 0 });
        setFocusPanelHidden(true);
        showToast('Timer cleared');
      }
      return;
    }
    try {
      await withBusy('Stopping timer...', async () => {
        await Api.post(`/tasks/${activeTimer.task_id}/timer/stop`, {});
        setPausedTimerState(null);
        setTimerCarry({ taskId: null, seconds: 0 });
        await loadTasks();
        showToast('Timer stopped');
      });
    } catch (err) {
      handleError(err);
    }
  }

  function applyDateChip(chip) {
    const target = chip === 'tomorrow'
      ? Dates.addDays(new Date(), 1)
      : chip === 'weekend'
        ? Dates.weekendDate()
        : new Date();
    target.setHours(17, 0, 0, 0);
    setDrawer((current) => current.is_all_day
      ? { ...current, due_date: Dates.localDateKey(target) }
      : { ...current, due_at_local: Dates.localInput(target) });
  }

  function openSettings() {
    clearErrors();
    setContextMenu(null);
    setDrawerMode('settings');
    setDrawerOpen(true);
    setSettingsDraft({
      ...settings,
      default_reminder_offsets: [...(settings?.default_reminder_offsets || [-1440, -30, 0])],
    });
  }

  async function saveSettings() {
    clearErrors();
    setSaving(true);
    try {
      await withBusy('Saving settings...', async () => {
        const data = await Api.patch('/settings', settingsDraft);
        setSettings(data.settings);
        showToast('Settings saved');
        await loadTasks();
      });
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  }

  async function enableNotifications() {
    clearErrors();
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      showToast('Browser notifications are not supported here');
      return;
    }

    try {
      await withBusy('Enabling browser notifications...', async () => {
        const config = await Api.get('/push/config');
        if (!config.configured || !config.public_key) {
          updateSettingsDraft({ browser_notifications_enabled: false });
          showToast('Add VAPID keys on the server to enable push');
          return;
        }

        const permission = Notification.permission === 'granted'
          ? 'granted'
          : await Notification.requestPermission();

        if (permission !== 'granted') {
          updateSettingsDraft({ browser_notifications_enabled: false });
          showToast('Permission was not granted');
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(config.public_key),
          });
        }

        await Api.post('/push/subscribe', pushSubscriptionPayload(subscription));
        const data = await Api.patch('/settings', { browser_notifications_enabled: true });
        setSettings(data.settings);
        updateSettingsDraft({ browser_notifications_enabled: true });
        showToast('Browser notifications enabled');
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function syncCalendar() {
    try {
      await withBusy('Queueing calendar sync...', async () => {
        const data = await Api.post('/calendar/sync', {});
        showToast(`Calendar sync complete: ${Number(data.synced || 0)} synced`);
        await loadBootstrap();
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function connectCalendar() {
    try {
      await withBusy('Opening calendar connection...', async () => {
        const data = await Api.post('/calendar/connect', {});
        if (data.auth_url) {
          window.location.assign(data.auth_url);
          return;
        }
        showToast('Google Calendar is ready to connect');
      });
    } catch (err) {
      handleError(err);
    }
  }

  async function readNotification(notification) {
    try {
      await withBusy('Marking notification read...', async () => {
        await Api.post(`/notifications/${notification.id}/read`, {});
        await loadBootstrap();
      });
    } catch (err) {
      handleError(err);
    }
  }

  function currentListIdForCreate() {
    return selectedView === 'list' ? selectedListId : null;
  }

  function offsetsFromReminders(reminders) {
    const offsets = (reminders || [])
      .filter((reminder) => reminder.offset_minutes !== null && reminder.offset_minutes !== undefined)
      .map((reminder) => Number(reminder.offset_minutes));
    return offsets.length ? [...new Set(offsets)] : [...(settings?.default_reminder_offsets || [-1440, -30, 0])];
  }

  function setDrawerReminder(minutes, enabled) {
    setDrawer((current) => ({
      ...current,
      reminder_offsets: toggleOffset(current.reminder_offsets || [], minutes, enabled),
    }));
  }

  function setDefaultReminder(minutes, enabled) {
    setSettingsDraft((current) => ({
      ...current,
      default_reminder_offsets: toggleOffset(current?.default_reminder_offsets || [], minutes, enabled),
    }));
  }

  function updateDrawer(values) {
    setDrawer((current) => ({ ...current, ...values }));
  }

  function updateSettingsDraft(values) {
    setSettingsDraft((current) => ({ ...current, ...values }));
  }

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function fieldError(field) {
    return fieldErrors?.[field] || '';
  }

  function clearErrors() {
    setError('');
    setFieldErrors({});
  }

  function handleError(err) {
    if (err?.status === 401) {
      setSession({ authenticated: false, user: null });
    }
    if (err?.status === 419) {
      loadSession();
    }
    const fields = { ...(err?.details?.fields || {}) };
    if (err?.code === 'setup_token_invalid') {
      fields.setup_token = 'This must exactly match APP_SETUP_TOKEN in your .env file.';
    }
    setFieldErrors(fields);
    setError(err?.message || 'Something went wrong.');
  }

  function showToast(message, action = null) {
    setToast({ message, action });
    setTimeout(() => {
      setToast((current) => (current?.message === message ? null : current));
    }, 5000);
  }

  async function withBusy(message, work) {
    setBusyMessage(message);
    try {
      return await work();
    } finally {
      setBusyMessage('');
    }
  }

  function menuPlacement(point = {}) {
    const x = Number(point.x || 0);
    const y = Number(point.y || 0);
    const horizontal = x && x > window.innerWidth / 2 ? 'right' : 'left';
    const vertical = y && y > window.innerHeight / 2 ? 'bottom' : 'top';
    return `${vertical}-${horizontal}`;
  }

  function openListMenu(list, point) {
    setNotificationOpen(false);
    setContextMenu({ type: 'list', item: list, placement: menuPlacement(point) });
  }

  function openTaskMenu(task, point) {
    setNotificationOpen(false);
    setContextMenu({ type: 'task', item: task, placement: menuPlacement(point) });
  }

  function runMenuAction(action) {
    setContextMenu(null);
    action();
  }

  function openDialog(config) {
    return new Promise((resolve) => {
      dialogResolver.current = resolve;
      setDialog(config);
    });
  }

  function resolveDialog(value) {
    const resolver = dialogResolver.current;
    dialogResolver.current = null;
    setDialog(null);
    if (resolver) resolver(value);
  }

  if (booting) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>TaskFlow</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!session.authenticated) {
    return setupRequired ? (
      <>
        <div className="auth-screen">
          <form className="auth-card" onSubmit={setupOwner}>
            <h1>TaskFlow</h1>
            <p>Owner setup</p>
            {error && <div className="alert">{error}</div>}
            <label className="field" htmlFor="setup-token">
              <span>Setup token</span>
              <input id="setup-token" className={cx('input', fieldError('setup_token') && 'invalid')} value={setupForm.setup_token} onChange={(event) => setSetupForm({ ...setupForm, setup_token: event.target.value })} autoComplete="one-time-code" placeholder="Paste your setup token" required />
              <FieldError message={fieldError('setup_token')} />
            </label>
            <label className="field" htmlFor="setup-email">
              <span>Email</span>
              <input id="setup-email" className={cx('input', fieldError('email') && 'invalid')} type="email" value={setupForm.email} onChange={(event) => setSetupForm({ ...setupForm, email: event.target.value })} autoComplete="email" placeholder="owner@example.com" required />
              <FieldError message={fieldError('email')} />
            </label>
            <label className="field" htmlFor="setup-name">
              <span>Display name</span>
              <input id="setup-name" className={cx('input', fieldError('display_name') && 'invalid')} value={setupForm.display_name} onChange={(event) => setSetupForm({ ...setupForm, display_name: event.target.value })} placeholder="Your name" required />
              <FieldError message={fieldError('display_name')} />
            </label>
            <label className="field" htmlFor="setup-timezone">
              <span>Time zone</span>
              <input id="setup-timezone" className={cx('input', fieldError('timezone') && 'invalid')} value={setupForm.timezone} onChange={(event) => setSetupForm({ ...setupForm, timezone: event.target.value })} placeholder="e.g. America/Chicago" required />
              <FieldError message={fieldError('timezone')} />
            </label>
            <label className="field" htmlFor="setup-password">
              <span>Password</span>
              <input id="setup-password" className={cx('input', fieldError('password') && 'invalid')} type="password" value={setupForm.password} onChange={(event) => setSetupForm({ ...setupForm, password: event.target.value })} autoComplete="new-password" placeholder="At least 12 characters" minLength="12" required />
              {fieldError('password') ? <FieldError message={fieldError('password')} /> : <span className="field-hint">At least 12 characters.</span>}
            </label>
            <button className="primary-button" disabled={saving} type="submit">{saving ? 'Creating owner...' : 'Create owner'}</button>
          </form>
        </div>
        {busyMessage && <BusyOverlay message={busyMessage} />}
      </>
    ) : (
      <>
        <div className="auth-screen">
          <form className="auth-card" onSubmit={login}>
            <h1>TaskFlow</h1>
            <p>Sign in</p>
            {error && <div className="alert">{error}</div>}
            <label className="field" htmlFor="email">
              <span>Email</span>
              <input id="email" className="input" type="email" value={loginForm.email} onChange={(event) => setLoginForm({ ...loginForm, email: event.target.value })} autoComplete="email" placeholder="owner@example.com" required />
            </label>
            <label className="field" htmlFor="password">
              <span>Password</span>
              <input id="password" className="input" type="password" value={loginForm.password} onChange={(event) => setLoginForm({ ...loginForm, password: event.target.value })} autoComplete="current-password" placeholder="Enter your password" required />
            </label>
            <button className="primary-button" disabled={saving} type="submit">{saving ? 'Signing in...' : 'Sign in'}</button>
          </form>
        </div>
        {busyMessage && <BusyOverlay message={busyMessage} />}
      </>
    );
  }

  const actionBusy = Boolean(busyMessage);

  return (
    <div className="app-shell" aria-busy={actionBusy || loading}>
      <Sidebar
        selectedView={selectedView}
        selectedListId={selectedListId}
        smartCounts={smartCounts}
        lists={lists}
        onSelectView={selectView}
        onSelectList={selectList}
        onCreateList={createList}
        onRenameList={renameList}
        onDeleteList={deleteListFlow}
        onListMenu={openListMenu}
        onNewTask={openNewDetails}
        onSettings={openSettings}
        onLogout={logout}
      />

      <main className="main">
        <div className="mobile-bar">
          <button className="icon-button" type="button" onClick={() => setSidebarOpen((value) => !value)} aria-label="Lists"><Icon name="menu" /></button>
          <h1>{viewTitle}</h1>
          <button className="icon-button" type="button" onClick={() => setNotificationOpen((value) => !value)} aria-label="Notifications">
            <Icon name="bell" />{unreadCount ? <span className="badge-dot" /> : null}
          </button>
          <button className="icon-button" type="button" onClick={openSettings} aria-label="Settings"><Icon name="settings" /></button>
        </div>

        <header className="topbar">
          <div className="topbar-title">
            <h1>{viewTitle}</h1>
            <p>{viewSubtitle}</p>
          </div>
          <div className="top-actions">
            <button className="ghost-button" type="button" disabled={loading || actionBusy} onClick={() => loadTasks()}><Icon name="refresh" /> {loading ? 'Refreshing...' : 'Refresh'}</button>
            <button className="primary-button" type="button" disabled={actionBusy} onClick={openNewDetails}><Icon name="plus" /> New task</button>
            <button className="icon-button" type="button" onClick={() => setNotificationOpen((value) => !value)} aria-label="Notifications">
              <Icon name="bell" />{unreadCount ? <span className="badge-dot" /> : null}
            </button>
            <button className="icon-button" type="button" onClick={openSettings} aria-label="Settings"><Icon name="settings" /></button>
          </div>
        </header>

        <div className="toolbar">
          <div className="field search-field">
            <span className="field-label">Search</span>
            <Icon name="search" />
            <input className="input" value={query} onChange={(event) => queueLoadTasks(event.target.value)} placeholder="Search tasks, notes, or lists" aria-label="Search tasks" />
            {query && (
              <button className="icon-button search-clear" type="button" onClick={clearSearch} aria-label="Clear search">
                <Icon name="x" />
              </button>
            )}
          </div>
          <label className="field">
            <span className="field-label">Sort</span>
            <select className="select" value={sort} onChange={(event) => { const nextSort = event.target.value; setSort(nextSort); loadTasks({ sort: nextSort }); }}>
              <option value="due">Due and start</option>
              <option value="priority">Priority</option>
              <option value="updated">Updated</option>
              <option value="created">Created</option>
              <option value="title">Title</option>
            </select>
          </label>
          {loading && tasks.length > 0 && (
            <div className="loading-strip" role="status">
              <span className="spinner" />
              <span>Updating...</span>
            </div>
          )}
        </div>

        <section className="workspace" aria-live="polite">
          <div className="content-width">
            {error && <div className="alert">{error}</div>}
            {loading && tasks.length === 0 ? (
              <>
                <div className="skeleton" />
                <div className="skeleton" />
                <div className="skeleton" />
              </>
            ) : tasks.length === 0 ? (
              <EmptyState selectedView={selectedView} query={query} viewTitle={viewTitle} />
            ) : (
              taskGroups.map((group) => (
                <section key={group.title} className="group">
                  <div className="group-title">
                    <span>{group.title}</span>
                    <span>{group.items.length}</span>
                  </div>
                  <div className="task-list">
                    {group.items.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        pausedTimerTaskId={visibleTimerPaused ? visibleTimerTaskId : null}
                        selectedView={selectedView}
                        onOpen={openTask}
                        onToggle={toggleComplete}
                        onTimer={timerAction}
                        onMenu={openTaskMenu}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </div>
        </section>

        <form className="quick-capture" onSubmit={quickAdd}>
          <input className="input" value={quickTitle} onChange={(event) => setQuickTitle(event.target.value)} placeholder="Add a task, e.g. Review Daily Order" autoComplete="off" disabled={actionBusy} />
          <button className="ghost-button" type="button" disabled={actionBusy} onClick={openNewDetails}><Icon name="plus" /> Task details</button>
          <button className="primary-button quick-submit" type="submit" disabled={quickBusy || actionBusy || !quickTitle.trim()} aria-label="Add task"><Icon name="plus" /><span>{quickBusy ? 'Adding...' : 'Add'}</span></button>
        </form>

        <nav className="bottom-nav" aria-label="Primary">
          <button type="button" className={cx(selectedView === 'today' && 'active')} onClick={() => selectView('today')}><Icon name="today" />Today</button>
          <button type="button" className={cx(selectedView === 'upcoming' && 'active')} onClick={() => selectView('upcoming')}><Icon name="upcoming" />Upcoming</button>
          <button type="button" className={cx(selectedView === 'inbox' && 'active')} onClick={() => selectView('inbox')}><Icon name="inbox" />Inbox</button>
          <button type="button" className={cx((visibleTimer || selectedView === 'all') && 'active')} onClick={() => visibleTimer ? setFocusPanelHidden(false) : selectView('all')}><Icon name={visibleTimer ? 'clock' : 'all'} />{visibleTimer ? 'Timer' : 'All'}</button>
        </nav>
      </main>

      {visibleTimer && !focusPanelHidden && (
        <FocusOverlay
          timer={visibleTimer}
          elapsed={visibleTimerElapsed}
          isPaused={visibleTimerPaused}
          position={timerPosition}
          todaySeconds={timerTotals.today_seconds}
          onMove={saveTimerPosition}
          onPause={() => pauseTimer()}
          onResume={() => resumePausedTimer()}
          onStop={stopActiveTimer}
          onHide={() => setFocusPanelHidden(true)}
        />
      )}

      {visibleTimer && focusPanelHidden && (
        <div className={cx('active-timer', visibleTimerPaused && 'paused', timerPosition && 'placed')} style={timerPositionStyle(timerPosition)} role="status">
          <Icon name="clock" />
          <div>
            <div className="timer-title">{visibleTimer.task_title}</div>
            <div className="muted">{formatDuration(visibleTimerElapsed)} {visibleTimerPaused ? 'paused' : 'running'}</div>
          </div>
          <button className="icon-button" type="button" onClick={() => setFocusPanelHidden(false)} aria-label="Show focus timer"><Icon name="clock" /></button>
        </div>
      )}

      {notificationOpen && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          onRead={readNotification}
        />
      )}

      {drawerOpen && <div className="drawer-backdrop" onClick={closeDrawer} />}
      {drawerOpen && drawerMode === 'task' && (
        <TaskDrawer
          drawer={drawer}
          drawerTaskId={drawerTaskId}
          saving={saving}
          lists={lists}
          calendar={calendar}
          fieldError={fieldError}
          onChange={updateDrawer}
          onClose={closeDrawer}
          onSave={saveDrawer}
          onDelete={deleteTask}
          onTimer={timerAction}
          onStopTimer={stopActiveTimer}
          pausedTimerTaskId={visibleTimerPaused ? visibleTimerTaskId : null}
          onDateChip={applyDateChip}
          onReminder={setDrawerReminder}
        />
      )}
      {drawerOpen && drawerMode === 'settings' && settingsDraft && (
        <SettingsDrawer
          session={session}
          settingsDraft={settingsDraft}
          calendar={calendar}
          saving={saving}
          onChange={updateSettingsDraft}
          onReminder={setDefaultReminder}
          onEnableNotifications={enableNotifications}
          onConnectCalendar={connectCalendar}
          onSyncCalendar={syncCalendar}
          onClose={closeDrawer}
          onLogout={logout}
          onSave={saveSettings}
        />
      )}

      {sidebarOpen && (
        <MobileMenu
          selectedView={selectedView}
          selectedListId={selectedListId}
          smartCounts={smartCounts}
          lists={lists}
          onClose={() => setSidebarOpen(false)}
          onSelectView={selectView}
          onSelectList={selectList}
          onCreateList={createList}
          onRenameList={renameList}
          onDeleteList={deleteListFlow}
          onListMenu={openListMenu}
        />
      )}

      {toast && (
        <div className="toast" role="status">
          <span>{toast.message}</span>
          {toast.action && <button className="ghost-button" type="button" onClick={restoreUndo}><Icon name="undo" /> {toast.action}</button>}
        </div>
      )}

      {contextMenu && (
        <ActionMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
          onOpenTask={(task) => runMenuAction(() => openTask(task))}
          onToggleTask={(task) => runMenuAction(() => toggleComplete(task))}
          onTimerTask={(task) => runMenuAction(() => timerAction(task))}
          onDeleteTask={(task) => runMenuAction(() => deleteTask(task))}
          pausedTimerTaskId={visibleTimerPaused ? visibleTimerTaskId : null}
          onSelectList={(list) => runMenuAction(() => selectList(list.id))}
          onNewTaskInList={(list) => runMenuAction(() => openNewDetailsForList(Number(list.id)))}
          onRenameList={(list) => runMenuAction(() => renameList(list))}
          onDeleteList={(list) => runMenuAction(() => deleteListFlow(list))}
        />
      )}

      {busyMessage && <BusyOverlay message={busyMessage} />}

      <DialogLayer dialog={dialog} onResolve={resolveDialog} />
    </div>
  );
}

function Sidebar(props) {
  const {
    selectedView,
    selectedListId,
    smartCounts,
    lists,
    onSelectView,
    onSelectList,
    onCreateList,
    onRenameList,
    onDeleteList,
    onListMenu,
    onNewTask,
    onSettings,
    onLogout,
  } = props;

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-name">TaskFlow</div>
        <button className="icon-button" type="button" onClick={onLogout} aria-label="Sign out"><Icon name="logout" /></button>
      </div>
      <nav className="side-section" aria-label="Smart views">
        <div className="side-title">Views</div>
        {smartViews.map((view) => (
          <button key={view.id} className={cx('nav-button', selectedView === view.id && 'active')} type="button" onClick={() => onSelectView(view.id)}>
            <Icon name={view.icon} />
            <span className="nav-label">{view.label}</span>
            <span className="count-pill">{smartCounts[view.id] || 0}</span>
          </button>
        ))}
      </nav>
      <nav className="side-section lists-section" aria-label="Custom lists">
        <div className="side-title-row">
          <div className="side-title">Lists</div>
          <button className="icon-button" type="button" onClick={onCreateList} aria-label="Add list"><Icon name="plus" /></button>
        </div>
        <div className="list-scroll">
          {lists.map((list) => (
            <ListNavItem
              key={list.id}
              list={list}
              active={selectedView === 'list' && Number(selectedListId) === Number(list.id)}
              onSelect={onSelectList}
              onRename={onRenameList}
              onDelete={onDeleteList}
              onMenu={onListMenu}
            />
          ))}
        </div>
      </nav>
      <div className="sidebar-actions">
        <button className="primary-button" type="button" onClick={onNewTask}><Icon name="plus" /> New task</button>
        <button className="ghost-button" type="button" onClick={onSettings}><Icon name="settings" /> Settings</button>
      </div>
    </aside>
  );
}

function useLongPressMenu(onOpen) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const openedRef = useRef(false);

  function cancel() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }

  return {
    onPointerDown(event) {
      if (event.pointerType === 'mouse' || event.button !== 0) return;
      const point = { x: event.clientX, y: event.clientY };
      openedRef.current = false;
      startRef.current = point;
      timerRef.current = window.setTimeout(() => {
        openedRef.current = true;
        navigator.vibrate?.(12);
        onOpen(point);
      }, 560);
    },
    onPointerMove(event) {
      if (!startRef.current) return;
      const dx = Math.abs(event.clientX - startRef.current.x);
      const dy = Math.abs(event.clientY - startRef.current.y);
      if (dx > 10 || dy > 10) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onClickCapture(event) {
      if (!openedRef.current) return;
      openedRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}

function ListNavItem({ list, active, onSelect, onRename, onDelete, onMenu, menuOnly = false }) {
  const longPress = useLongPressMenu((point) => onMenu(list, point));
  function openMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    onMenu(list, { x: event.clientX, y: event.clientY });
  }

  return (
    <div className={cx('list-line', menuOnly && 'menu-only')} onContextMenu={openMenu} {...longPress}>
      <button className={cx('list-button', active && 'active')} type="button" onClick={() => onSelect(list.id)} title={list.name}>
        <Icon name="list" />
        <span className="nav-label">{list.name}</span>
        <span className="count-pill">{list.active_count || 0}</span>
      </button>
      <div className="list-actions">
        {menuOnly ? (
          <button className="icon-button" type="button" onClick={openMenu} aria-label={`${list.name} actions`}><Icon name="more" /></button>
        ) : (
          <>
            <button className="icon-button" type="button" onClick={() => onRename(list)} aria-label={`Rename ${list.name}`}><Icon name="edit" /></button>
            <button className="icon-button" type="button" onClick={() => onDelete(list)} aria-label={`Delete ${list.name}`}><Icon name="trash" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function TaskRow({ task, pausedTimerTaskId, selectedView, onOpen, onToggle, onTimer, onMenu }) {
  const dateBadge = Dates.friendlyTaskDate(task);
  const longPress = useLongPressMenu((point) => onMenu(task, point));
  const timerPaused = Number(pausedTimerTaskId) === Number(task.id);
  const timerRunning = Boolean(task.running_timer_started_at_utc);
  const timerLabel = timerRunning ? 'Pause timer' : timerPaused ? 'Resume timer' : 'Start timer';
  function openMenu(event) {
    event.preventDefault();
    event.stopPropagation();
    onMenu(task, { x: event.clientX, y: event.clientY });
  }

  return (
    <article className={cx('task-row', `priority-${task.priority || 'normal'}`, task.completed && 'completed')} onContextMenu={openMenu} {...longPress}>
      <input className="task-check" type="checkbox" checked={!!task.completed} onChange={() => onToggle(task)} aria-label={`Complete ${task.title}`} />
      <button className="task-main" type="button" onClick={() => onOpen(task)}>
        <span className="task-title">{task.title}</span>
        <span className="task-meta">
          {task.priority !== 'normal' && <span className="badge">{task.priority_label}</span>}
          {task.list_name && selectedView !== 'list' && <span className="badge">{task.list_name}</span>}
          {dateBadge && <span className={cx('badge', dateBadge.tone)}>{dateBadge.text}</span>}
          {(task.reminders?.length || task.due_at_utc || task.due_date) && <span className="badge"><Icon name="bell" /> Reminders</span>}
          {task.calendar_sync_enabled && <span className="badge"><Icon name="calendar" /> {task.calendar_sync_status || 'Queued'}</span>}
          {timerRunning && <span className="badge running"><Icon name="clock" /> Running</span>}
          {timerPaused && <span className="badge paused"><Icon name="clock" /> Paused</span>}
          {task.tracked_seconds ? <span className="badge"><Icon name="clock" /> {formatDuration(task.tracked_seconds)}</span> : null}
        </span>
      </button>
      <div className="task-actions">
        <button className="icon-button timer-action" type="button" onClick={() => onTimer(task)} aria-label={timerLabel}>
          <Icon name={timerRunning ? 'pause' : 'play'} />
        </button>
        <button className="icon-button edit-action" type="button" onClick={() => onOpen(task)} aria-label="Edit task"><Icon name="edit" /></button>
        <button className="icon-button more-action" type="button" onClick={openMenu} aria-label="Task actions"><Icon name="more" /></button>
      </div>
    </article>
  );
}

function EmptyState({ selectedView, query, viewTitle }) {
  const title = emptyTitle(selectedView, query, viewTitle);
  const hint = emptyHint(selectedView, query);
  return (
    <div className="empty-state">
      <Icon name={selectedView === 'overdue' ? 'clock' : 'inbox'} />
      <strong>{title}</strong>
      <span>{hint}</span>
    </div>
  );
}

function FocusOverlay({ timer, elapsed, isPaused, position, todaySeconds, onMove, onPause, onResume, onStop, onHide }) {
  const overlayRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const targetSeconds = Math.max(60, Number(timer.estimated_minutes || 25) * 60);
  const remaining = targetSeconds - elapsed;
  const overtime = remaining < 0;
  const progress = Math.min(100, Math.max(0, (elapsed / targetSeconds) * 100));
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference * (1 - progress / 100);
  const display = formatClock(Math.abs(remaining));

  function startDrag(event) {
    if (!canDragTimer() || !overlayRef.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
    const rect = overlayRef.current.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    event.preventDefault();
  }

  function moveDrag(event) {
    const drag = dragRef.current;
    if (!drag || !overlayRef.current) return;
    const rect = overlayRef.current.getBoundingClientRect();
    onMove(clampTimerPosition({
      x: event.clientX - drag.offsetX,
      y: event.clientY - drag.offsetY,
    }, rect));
  }

  function endDrag(event) {
    if (!dragRef.current) return;
    event.currentTarget.releasePointerCapture?.(dragRef.current.pointerId);
    dragRef.current = null;
    setDragging(false);
  }

  function moveWithKeyboard(event) {
    const deltas = {
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
    };
    const delta = deltas[event.key];
    if (!delta || !canDragTimer() || !overlayRef.current) return;
    event.preventDefault();
    const rect = overlayRef.current.getBoundingClientRect();
    const step = event.shiftKey ? 40 : 16;
    onMove(clampTimerPosition({
      x: rect.left + delta[0] * step,
      y: rect.top + delta[1] * step,
    }, rect));
  }

  return (
    <section
      ref={overlayRef}
      className={cx('focus-overlay', overtime && 'overtime', isPaused && 'paused', position && 'placed', dragging && 'dragging')}
      style={timerPositionStyle(position)}
      role="status"
      aria-live="polite"
    >
      <button
        className="focus-drag-handle"
        type="button"
        title="Move timer"
        aria-label="Move timer"
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={moveWithKeyboard}
      >
        <Icon name="move" />
      </button>
      <div className="focus-ring">
        <svg className="focus-ring-svg" viewBox="0 0 132 132" aria-hidden="true">
          <circle className="focus-ring-track" cx="66" cy="66" r={radius} />
          <circle className="focus-ring-meter" cx="66" cy="66" r={radius} strokeDasharray={circumference} strokeDashoffset={strokeOffset} />
        </svg>
        <div className="focus-clock">{display}</div>
        <div className="focus-caption">{isPaused ? 'Paused' : overtime ? 'Over estimate' : 'Remaining'}</div>
      </div>
      <div className="focus-copy">
        <span className="field-label">Focus timer</span>
        <strong>{timer.task_title}</strong>
        <span className="muted">{formatDuration(elapsed)} {isPaused ? 'paused' : 'tracked'} today {formatDuration(todaySeconds)}</span>
      </div>
      <div className="focus-actions">
        <button className="ghost-button" type="button" onClick={isPaused ? onResume : onPause}><Icon name={isPaused ? 'play' : 'pause'} /> {isPaused ? 'Resume' : 'Pause'}</button>
        <button className="danger-button" type="button" onClick={onStop}><Icon name="stop" /> Stop</button>
        <button className="icon-button" type="button" onClick={onHide} aria-label="Hide timer"><Icon name="eyeOff" /></button>
      </div>
    </section>
  );
}

function NotificationPanel({ notifications, unreadCount, onRead }) {
  return (
    <aside className="notification-panel" aria-label="Notifications">
      <div className="group-title"><span>Notifications</span><span>{unreadCount}</span></div>
      {!notifications.items.length && <div className="empty-state">No notifications.</div>}
      {notifications.items.map((item) => (
        <div key={item.id} className="notification-item">
          <strong>{item.title}</strong>
          <span className="muted">{item.body}</span>
          <button className="ghost-button" type="button" onClick={() => onRead(item)}>Mark read</button>
        </div>
      ))}
    </aside>
  );
}

function TaskDrawer(props) {
  const {
    drawer,
    drawerTaskId,
    saving,
    lists,
    calendar,
    fieldError,
    onChange,
    onClose,
    onSave,
    onDelete,
    onTimer,
    onStopTimer,
    pausedTimerTaskId,
    onDateChip,
    onReminder,
  } = props;
  const drawerTimerPaused = Number(pausedTimerTaskId) === Number(drawerTaskId);
  const drawerTimerRunning = Boolean(drawer.running_timer_started_at_utc);
  const drawerTimerLabel = drawerTimerRunning ? 'Pause timer' : drawerTimerPaused ? 'Resume timer' : 'Start timer';

  return (
    <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="task-drawer-title">
      <header className="drawer-header">
        <div className="drawer-title">
          <h2 id="task-drawer-title">{drawerTaskId ? 'Task details' : 'New task'}</h2>
          <p>{saving ? 'Saving' : 'Ready'}</p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
      </header>
      <div className="drawer-body">
        <label className="field">
          <span>Title</span>
          <input className={cx('input', fieldError('text') && 'invalid')} value={drawer.text || ''} onChange={(event) => onChange({ text: event.target.value })} placeholder="e.g. Follow up with Daily Order" required />
          <FieldError message={fieldError('text')} />
        </label>
        <label className="field">
          <span>Notes</span>
          <textarea className="textarea" value={drawer.notes || ''} onChange={(event) => onChange({ notes: event.target.value })} placeholder="Add context, links, phone numbers, or next steps" />
        </label>
        <div className="grid-2">
          <label className="field">
            <span>List</span>
            <select className={cx('select', fieldError('list_id') && 'invalid')} value={drawer.list_id || ''} onChange={(event) => onChange({ list_id: event.target.value ? Number(event.target.value) : null })}>
              <option value="">Inbox</option>
              {lists.map((list) => <option key={list.id} value={list.id}>{list.name}</option>)}
            </select>
            <FieldError message={fieldError('list_id')} />
          </label>
          <label className="field">
            <span>Priority</span>
            <select className="select" value={drawer.priority || 'normal'} onChange={(event) => onChange({ priority: event.target.value })}>
              <option value="normal">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>
        <div className="chip-row">
          <button className="chip" type="button" onClick={() => onDateChip('today')}>Today</button>
          <button className="chip" type="button" onClick={() => onDateChip('tomorrow')}>Tomorrow</button>
          <button className="chip" type="button" onClick={() => onDateChip('weekend')}>This weekend</button>
        </div>
        <div className="grid-2 drawer-date-grid">
          <label className="field">
            <span>Start</span>
            <DateInput type="datetime-local" value={drawer.scheduled_start_local || ''} onChange={(event) => onChange({ scheduled_start_local: event.target.value })} placeholder="Start date and time" />
          </label>
          <label className="field">
            <span>Time zone</span>
            <input className="input" value={drawer.timezone || ''} onChange={(event) => onChange({ timezone: event.target.value })} placeholder="e.g. America/Chicago" autoComplete="off" />
          </label>
        </div>
        <label className="toggle-row">
          <span><strong>All-day deadline</strong></span>
          <input className="switch" type="checkbox" checked={!!drawer.is_all_day} onChange={(event) => onChange({ is_all_day: event.target.checked })} />
        </label>
        <div className="grid-2 drawer-date-grid">
          {drawer.is_all_day ? (
            <label className="field">
              <span>Due date</span>
              <DateInput className={cx('input', fieldError('due_date') && 'invalid')} type="date" value={drawer.due_date || ''} onChange={(event) => onChange({ due_date: event.target.value })} placeholder="Due date" />
              <FieldError message={fieldError('due_date')} />
            </label>
          ) : (
            <label className="field">
              <span>Deadline</span>
              <DateInput className={cx('input', fieldError('due_at_local') && 'invalid')} type="datetime-local" value={drawer.due_at_local || ''} onChange={(event) => onChange({ due_at_local: event.target.value })} placeholder="Deadline date and time" />
              <FieldError message={fieldError('due_at_local')} />
            </label>
          )}
          <label className="field">
            <span>Estimate</span>
            <input className={cx('input', fieldError('estimated_minutes') && 'invalid')} type="number" min="1" max="10080" step="1" value={drawer.estimated_minutes || ''} onChange={(event) => onChange({ estimated_minutes: event.target.value })} placeholder="e.g. 30 minutes" />
            <FieldError message={fieldError('estimated_minutes')} />
          </label>
        </div>
        <div className="sheet-section">
          <div className="field-label">Reminders</div>
          <ReminderToggle label="One day before" value={-1440} offsets={drawer.reminder_offsets} onChange={onReminder} />
          <ReminderToggle label="Thirty minutes before" value={-30} offsets={drawer.reminder_offsets} onChange={onReminder} />
          <ReminderToggle label="At deadline" value={0} offsets={drawer.reminder_offsets} onChange={onReminder} />
        </div>
        <label className="toggle-row">
          <span><strong>Add to Google Calendar</strong><br /><span>{calendar?.configured ? 'TaskFlow calendar' : 'Needs Google setup'}</span></span>
          <input className="switch" type="checkbox" checked={!!drawer.calendar_sync_enabled} onChange={(event) => onChange({ calendar_sync_enabled: event.target.checked })} />
        </label>
        {drawerTaskId && (
          <div className="sheet-section">
            <div className="field-label">Timer</div>
            <div className="chip-row">
              <button className="ghost-button" type="button" onClick={() => onTimer(drawer)}><Icon name={drawerTimerRunning ? 'pause' : 'play'} /> {drawerTimerLabel}</button>
              <button className="ghost-button" type="button" onClick={onStopTimer}><Icon name="stop" /> Stop timer</button>
              <span className="badge"><Icon name="clock" /> {formatDuration(drawer.tracked_seconds || 0)}</span>
            </div>
            {(drawer.time_entries || []).map((entry) => (
              <div key={entry.id} className="toggle-row">
                <span>{entry.started_at_utc}<br /><span>{entry.ended_at_utc || 'Running'}</span></span>
                <span>{formatDuration(entry.duration_seconds || 0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <footer className="drawer-footer">
        {drawerTaskId ? <button className="danger-button" type="button" disabled={saving} onClick={() => onDelete(drawer)}><Icon name="trash" /> Delete task</button> : <span />}
        <button className="primary-button" type="button" disabled={saving} onClick={onSave}><Icon name="save" /> {saving ? 'Saving task...' : 'Save task'}</button>
      </footer>
    </aside>
  );
}

function SettingsDrawer(props) {
  const {
    session,
    settingsDraft,
    calendar,
    saving,
    onChange,
    onReminder,
    onEnableNotifications,
    onConnectCalendar,
    onSyncCalendar,
    onClose,
    onLogout,
    onSave,
  } = props;

  return (
    <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="settings-title">
      <header className="drawer-header">
        <div className="drawer-title">
          <h2 id="settings-title">Settings</h2>
          <p>{session.user.email}</p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
      </header>
      <div className="drawer-body">
        <label className="field">
          <span>Time zone</span>
          <input className="input" value={settingsDraft.timezone || ''} onChange={(event) => onChange({ timezone: event.target.value })} placeholder="e.g. America/Chicago" autoComplete="off" />
        </label>
        <label className="field">
          <span>Theme</span>
          <select className="select" value={settingsDraft.theme || 'system'} onChange={(event) => onChange({ theme: event.target.value })}>
            <option value="system">System</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>
        <div className="sheet-section">
          <div className="field-label">Default reminders</div>
          <ReminderToggle label="One day before" value={-1440} offsets={settingsDraft.default_reminder_offsets} onChange={onReminder} />
          <ReminderToggle label="Thirty minutes before" value={-30} offsets={settingsDraft.default_reminder_offsets} onChange={onReminder} />
          <ReminderToggle label="At deadline" value={0} offsets={settingsDraft.default_reminder_offsets} onChange={onReminder} />
        </div>
        <div className="toggle-row">
          <span><strong>Browser notifications</strong><br /><span>{settingsDraft.browser_notifications_enabled ? 'Enabled on this browser' : 'Click to request permission'}</span></span>
          <button className="ghost-button" type="button" onClick={onEnableNotifications}><Icon name="bell" /> {settingsDraft.browser_notifications_enabled ? 'Enabled' : 'Enable'}</button>
        </div>
        <label className="toggle-row">
          <span><strong>Default calendar sync</strong><br /><span>{calendar?.configured ? 'Ready after connect' : 'Google OAuth values needed'}</span></span>
          <input className="switch" type="checkbox" checked={!!settingsDraft.default_calendar_sync_enabled} onChange={(event) => onChange({ default_calendar_sync_enabled: event.target.checked })} />
        </label>
        <div className="toggle-row">
          <span><strong>Google Calendar</strong><br /><span>{calendar?.connection?.status || (calendar?.configured ? 'Not connected' : 'Not configured')}</span></span>
          <div className="chip-row">
            <button className="ghost-button" type="button" onClick={onConnectCalendar}><Icon name="calendar" /> Connect</button>
            <button className="ghost-button" type="button" onClick={onSyncCalendar}><Icon name="refresh" /> Sync</button>
          </div>
        </div>
      </div>
      <footer className="drawer-footer">
        <button className="ghost-button" type="button" onClick={onLogout}><Icon name="logout" /> Sign out</button>
        <button className="primary-button" type="button" disabled={saving} onClick={onSave}><Icon name="save" /> {saving ? 'Saving settings...' : 'Save settings'}</button>
      </footer>
    </aside>
  );
}

function ReminderToggle({ label, value, offsets = [], onChange }) {
  return (
    <label className="toggle-row">
      <span>{label}</span>
      <input className="switch" type="checkbox" checked={(offsets || []).includes(value)} onChange={(event) => onChange(value, event.target.checked)} />
    </label>
  );
}

function MobileMenu(props) {
  const {
    selectedView,
    selectedListId,
    smartCounts,
    lists,
    onClose,
    onSelectView,
    onSelectList,
    onCreateList,
    onRenameList,
    onDeleteList,
    onListMenu,
  } = props;
  return (
    <aside className="mobile-menu-panel">
      <div className="group-title"><span>Menu</span><button className="icon-button" type="button" onClick={onClose} aria-label="Close"><Icon name="x" /></button></div>
      {smartViews.map((view) => (
        <button key={view.id} className={cx('nav-button', selectedView === view.id && 'active')} type="button" onClick={() => onSelectView(view.id)}>
          <Icon name={view.icon} /><span className="nav-label">{view.label}</span><span className="count-pill">{smartCounts[view.id] || 0}</span>
        </button>
      ))}
      {lists.map((list) => (
        <ListNavItem
          key={list.id}
          list={list}
          active={selectedView === 'list' && Number(selectedListId) === Number(list.id)}
          onSelect={onSelectList}
          onRename={onRenameList}
          onDelete={onDeleteList}
          onMenu={onListMenu}
          menuOnly
        />
      ))}
      <button className="ghost-button" type="button" onClick={onCreateList}><Icon name="plus" /> Add list</button>
    </aside>
  );
}

function ActionMenu(props) {
  const {
    menu,
    onClose,
    onOpenTask,
    onToggleTask,
    onTimerTask,
    onDeleteTask,
    pausedTimerTaskId,
    onSelectList,
    onNewTaskInList,
    onRenameList,
    onDeleteList,
  } = props;
  const item = menu.item;
  const title = menu.type === 'task' ? (item.title || item.text || 'Task') : item.name;
  const menuTimerPaused = menu.type === 'task' && Number(pausedTimerTaskId) === Number(item.id);
  const menuTimerRunning = menu.type === 'task' && Boolean(item.running_timer_started_at_utc);
  const menuTimerLabel = menuTimerRunning ? 'Pause timer' : menuTimerPaused ? 'Resume timer' : 'Start timer';

  return (
    <>
      <div className="context-backdrop" onClick={onClose} />
      <section className={cx('context-menu', menu.placement)} role="menu" aria-label={`${title} actions`}>
        <header className="context-header">
          <strong>{title}</strong>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close actions"><Icon name="x" /></button>
        </header>
        {menu.type === 'task' ? (
          <div className="context-actions">
            <button type="button" role="menuitem" onClick={() => onOpenTask(item)}><Icon name="edit" /> Open details</button>
            <button type="button" role="menuitem" onClick={() => onTimerTask(item)}><Icon name={menuTimerRunning ? 'pause' : 'play'} /> {menuTimerLabel}</button>
            <button type="button" role="menuitem" onClick={() => onToggleTask(item)}><Icon name="completed" /> {item.completed ? 'Reopen task' : 'Mark complete'}</button>
            <button className="danger" type="button" role="menuitem" onClick={() => onDeleteTask(item)}><Icon name="trash" /> Delete task</button>
          </div>
        ) : (
          <div className="context-actions">
            <button type="button" role="menuitem" onClick={() => onSelectList(item)}><Icon name="list" /> Open list</button>
            <button type="button" role="menuitem" onClick={() => onNewTaskInList(item)}><Icon name="plus" /> New task in list</button>
            <button type="button" role="menuitem" onClick={() => onRenameList(item)}><Icon name="edit" /> Rename list</button>
            <button className="danger" type="button" role="menuitem" onClick={() => onDeleteList(item)}><Icon name="trash" /> Delete list</button>
          </div>
        )}
      </section>
    </>
  );
}

function BusyOverlay({ message }) {
  return (
    <div className="busy-overlay" role="status" aria-live="assertive">
      <div className="busy-card">
        <span className="spinner large" />
        <strong>Please wait</strong>
        <span>{message}</span>
      </div>
    </div>
  );
}

function DialogLayer({ dialog, onResolve }) {
  if (!dialog) return null;
  if (dialog.type === 'list-name') {
    return <ListNameDialog dialog={dialog} onResolve={onResolve} />;
  }
  if (dialog.type === 'delete-list') {
    return <DeleteListDialog dialog={dialog} onResolve={onResolve} />;
  }
  return <ConfirmDialog dialog={dialog} onResolve={onResolve} />;
}

function ConfirmDialog({ dialog, onResolve }) {
  const confirmClass = dialog.tone === 'danger' ? 'danger-button' : 'primary-button';
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <header className="modal-header">
          <h2 id="confirm-title">{dialog.title}</h2>
          <button className="icon-button" type="button" onClick={() => onResolve(false)} aria-label="Close"><Icon name="x" /></button>
        </header>
        <p>{dialog.message}</p>
        <footer className="modal-footer">
          <button className="ghost-button" type="button" onClick={() => onResolve(false)}>Cancel</button>
          <button className={confirmClass} type="button" onClick={() => onResolve(true)}>{dialog.confirmLabel || 'Continue'}</button>
        </footer>
      </section>
    </div>
  );
}

function ListNameDialog({ dialog, onResolve }) {
  const [value, setValue] = useState(dialog.initialValue || '');
  const trimmed = value.trim();
  return (
    <div className="modal-backdrop" role="presentation">
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="list-name-title" onSubmit={(event) => { event.preventDefault(); if (trimmed) onResolve(trimmed); }}>
        <header className="modal-header">
          <h2 id="list-name-title">{dialog.title}</h2>
          <button className="icon-button" type="button" onClick={() => onResolve(null)} aria-label="Close"><Icon name="x" /></button>
        </header>
        <label className="field">
          <span>{dialog.label}</span>
          <input className="input" value={value} onChange={(event) => setValue(event.target.value)} placeholder="e.g. Daily Order" autoFocus />
        </label>
        <footer className="modal-footer">
          <button className="ghost-button" type="button" onClick={() => onResolve(null)}>Cancel</button>
          <button className="primary-button" type="submit" disabled={!trimmed}>{dialog.confirmLabel}</button>
        </footer>
      </form>
    </div>
  );
}

function DeleteListDialog({ dialog, onResolve }) {
  const [mode, setMode] = useState('move_to_inbox');
  const destinations = dialog.lists.filter((list) => Number(list.id) !== Number(dialog.list.id));
  const [targetListId, setTargetListId] = useState(destinations[0]?.id || '');
  const canSubmit = mode !== 'move_to_list' || targetListId;
  function payload() {
    if (mode === 'delete_tasks') return { mode: 'delete_tasks', confirm: 'DELETE TASKS' };
    if (mode === 'move_to_list') return { mode: 'move_to_list', target_list_id: Number(targetListId) };
    return { mode: 'move_to_inbox' };
  }
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-list-title">
        <header className="modal-header">
          <h2 id="delete-list-title">Delete list</h2>
          <button className="icon-button" type="button" onClick={() => onResolve(null)} aria-label="Close"><Icon name="x" /></button>
        </header>
        <p><strong>{dialog.list.name}</strong> has {Number(dialog.list.task_count || 0)} tasks. Choose where those tasks should go.</p>
        <div className="choice-list">
          <label className="choice-row">
            <input type="radio" name="delete-list-mode" checked={mode === 'move_to_inbox'} onChange={() => setMode('move_to_inbox')} />
            <span>Move tasks to Inbox</span>
          </label>
          <label className="choice-row">
            <input type="radio" name="delete-list-mode" checked={mode === 'move_to_list'} onChange={() => setMode('move_to_list')} />
            <span>Move tasks to another list</span>
          </label>
          {mode === 'move_to_list' && (
            <select className="select" value={targetListId} onChange={(event) => setTargetListId(event.target.value)}>
              {destinations.map((list) => <option key={list.id} value={list.id}>{list.name}</option>)}
            </select>
          )}
          <label className="choice-row danger-text">
            <input type="radio" name="delete-list-mode" checked={mode === 'delete_tasks'} onChange={() => setMode('delete_tasks')} />
            <span>Delete the tasks in this list</span>
          </label>
        </div>
        <footer className="modal-footer">
          <button className="ghost-button" type="button" onClick={() => onResolve(null)}>Cancel</button>
          <button className={mode === 'delete_tasks' ? 'danger-button' : 'primary-button'} type="button" disabled={!canSubmit} onClick={() => onResolve(payload())}>Delete list</button>
        </footer>
      </section>
    </div>
  );
}

function buildTaskGroups(tasks, selectedView) {
  if (!tasks.length) return [];
  if (selectedView === 'upcoming') {
    const grouped = new Map();
    tasks.forEach((task) => {
      const parsed = Dates.parseUtc(task.due_at_utc || task.scheduled_start_at_utc);
      const key = task.due_date || (parsed ? Dates.localDateKey(parsed) : 'Later');
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(task);
    });
    return [...grouped.entries()].map(([title, items]) => ({ title: groupTitle(title), items }));
  }
  if (selectedView === 'completed') return [{ title: 'Completed', items: tasks }];
  return [{ title: 'Tasks', items: tasks }];
}

function groupTitle(key) {
  if (key === 'Later') return 'Later';
  const today = Dates.localDateKey();
  const tomorrow = Dates.localDateKey(Dates.addDays(new Date(), 1));
  if (key === today) return 'Today';
  if (key === tomorrow) return 'Tomorrow';
  return Dates.formatter({ weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(`${key}T09:00`));
}

function emptyTitle(selectedView, query, viewTitle) {
  if (query.trim()) return 'No matches';
  if (selectedView === 'inbox') return 'Inbox is clear';
  if (selectedView === 'today') return 'Nothing due today';
  if (selectedView === 'upcoming') return 'Nothing scheduled';
  if (selectedView === 'overdue') return 'Nothing overdue';
  if (selectedView === 'completed') return 'No completed tasks yet';
  return `${viewTitle} is clear`;
}

function emptyHint(selectedView, query) {
  if (query.trim()) return 'Try a shorter search or clear the search field.';
  if (selectedView === 'inbox') return 'New unsorted tasks will land here.';
  if (selectedView === 'today') return 'Add a task below or schedule one from the details panel.';
  if (selectedView === 'upcoming') return 'Tasks with future start or due dates will appear here.';
  if (selectedView === 'overdue') return 'Past-due open tasks will appear here.';
  if (selectedView === 'completed') return 'Completed tasks will appear here after you check them off.';
  return 'Add a task below to get started.';
}

function toggleOffset(offsets, minutes, enabled) {
  const set = new Set(offsets || []);
  if (enabled) set.add(minutes);
  else set.delete(minutes);
  return [...set].sort((a, b) => a - b);
}

function formatDuration(seconds) {
  return Dates.duration(seconds);
}

function formatClock(seconds) {
  const safe = Math.max(0, Math.floor(Number(seconds || 0)));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  const pad = (value) => String(value).padStart(2, '0');
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(secs)}`;
  return `${pad(minutes)}:${pad(secs)}`;
}

createRoot(document.getElementById('app')).render(<App />);
